import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Tooltip,
    Chip,
    Collapse,
    Button,
    Divider
} from '@mui/material';
import {
    ContentCopy as CopyIcon,
    ExpandMore as ExpandIcon,
    ExpandLess as CollapseIcon,
    SwapHoriz as ConvertIcon
} from '@mui/icons-material';
import {
    smartConvert,
    copyToClipboard,
    formatLargeNumber,
    ConversionResult
} from '../../utils/hexConverter';

interface InlineConverterProps {
    value: string;
    label?: string;
    autoExpand?: boolean;
    showRawValue?: boolean;
    compact?: boolean;
    onOpenFloatingConverter?: (value: string) => void;
}

const InlineConverter: React.FC<InlineConverterProps> = ({
    value,
    label,
    autoExpand = false,
    showRawValue = true,
    compact = false,
    onOpenFloatingConverter
}) => {
    const [result, setResult] = useState<ConversionResult | null>(null);
    const [isExpanded, setIsExpanded] = useState(autoExpand);
    const [copySuccess, setCopySuccess] = useState<string>('');

    // Convert value when component mounts or value changes
    useEffect(() => {
        if (value && value.trim()) {
            try {
                const conversionResult = smartConvert(value.trim());
                setResult(conversionResult);
            } catch (error) {
                setResult(null);
            }
        } else {
            setResult(null);
        }
    }, [value]);

    // Handle copy to clipboard
    const handleCopy = async (text: string, label: string) => {
        const success = await copyToClipboard(text);
        if (success) {
            setCopySuccess(`${label} copied!`);
            setTimeout(() => setCopySuccess(''), 1500);
        }
    };

    // Don't render if no valid conversion result
    if (!result || result.confidence < 0.3) {
        return null;
    }

    // Compact mode - just show converted value inline
    if (compact) {
        return (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                <Typography variant="caption" color="text.secondary">
                    →
                </Typography>
                <Chip
                    label={result.formatted}
                    size="small"
                    variant="outlined"
                    color={result.confidence > 0.7 ? 'success' : 'default'}
                    onClick={() => handleCopy(result.formatted, 'Converted')}
                    sx={{ cursor: 'pointer', fontSize: '0.7rem', height: 20 }}
                />
                {onOpenFloatingConverter && (
                    <Tooltip title="Open in converter">
                        <IconButton
                            size="small"
                            onClick={() => onOpenFloatingConverter(value)}
                            sx={{ p: 0.25 }}
                        >
                            <ConvertIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        );
    }

    // Full mode - expandable conversion details
    return (
        <Box sx={{ mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1,
                    bgcolor: 'grey.50',
                    cursor: 'pointer'
                }}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                        label={result.type.toUpperCase()}
                        size="small"
                        color={result.confidence > 0.7 ? 'success' : result.confidence > 0.4 ? 'warning' : 'default'}
                    />
                    <Typography variant="body2" color="text.secondary">
                        {label || 'Converted Value'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        ({Math.round(result.confidence * 100)}% confidence)
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {copySuccess && (
                        <Typography variant="caption" color="success.main">
                            {copySuccess}
                        </Typography>
                    )}
                    <IconButton size="small">
                        {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
                    </IconButton>
                </Box>
            </Box>

            {/* Expandable Content */}
            <Collapse in={isExpanded}>
                <Box sx={{ p: 2 }}>
                    {/* Raw Value */}
                    {showRawValue && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary" gutterBottom>
                                Original:
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1, wordBreak: 'break-all', fontSize: '0.8rem' }}>
                                    {result.original}
                                </Typography>
                                <Tooltip title="Copy original">
                                    <IconButton size="small" onClick={() => handleCopy(result.original, 'Original')}>
                                        <CopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    )}

                    {/* Decimal Value */}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" gutterBottom>
                            Decimal:
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1, wordBreak: 'break-all', fontSize: '0.8rem' }}>
                                {result.decimal}
                            </Typography>
                            <Tooltip title="Copy decimal">
                                <IconButton size="small" onClick={() => handleCopy(result.decimal, 'Decimal')}>
                                    <CopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>

                    {/* Formatted Value */}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" gutterBottom>
                            Formatted:
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1, wordBreak: 'break-all', fontSize: '0.8rem' }}>
                                {result.formatted}
                            </Typography>
                            <Tooltip title="Copy formatted">
                                <IconButton size="small" onClick={() => handleCopy(result.formatted, 'Formatted')}>
                                    <CopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>

                    {/* Additional formats for amounts */}
                    {result.type === 'amount' && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary" gutterBottom>
                                Large Format:
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1, fontSize: '0.8rem' }}>
                                    {formatLargeNumber(result.decimal)}
                                </Typography>
                                <Tooltip title="Copy large format">
                                    <IconButton size="small" onClick={() => handleCopy(formatLargeNumber(result.decimal), 'Large format')}>
                                        <CopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    )}

                    {/* Suggestions */}
                    {result.suggestions.length > 0 && (
                        <Box>
                            <Typography variant="caption" color="text.secondary" gutterBottom>
                                Suggestions:
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {result.suggestions.slice(0, 2).map((suggestion, index) => (
                                    <Typography key={index} variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                        • {suggestion}
                                    </Typography>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* Actions */}
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {onOpenFloatingConverter && (
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<ConvertIcon />}
                                onClick={() => onOpenFloatingConverter(value)}
                            >
                                Open in Converter
                            </Button>
                        )}
                    </Box>
                </Box>
            </Collapse>
        </Box>
    );
};

export default InlineConverter;
